// Small display helpers shared by the dashboard, student profile, reports
// and print sheets.

export const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// 'YYYY-MM-DD' or ISO timestamp → '11 Oct 2026'
export function fmtDate(value) {
  if (!value) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function fmtTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

// 'YYYY-MM' → 'Oct 2026'
export function fmtMonth(ym) {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

// rows: array of plain objects; columns: [{ key, label }]
export function downloadCsv(filename, columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(c => esc(c.label)).join(',')]
    .concat(rows.map(r => columns.map(c => esc(r[c.key])).join(',')));
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// Common CBSE-style 8-point grade bands, by percentage.
export function gradeFor(pct) {
  if (pct == null || Number.isNaN(pct)) return '—';
  if (pct >= 91) return 'A1';
  if (pct >= 81) return 'A2';
  if (pct >= 71) return 'B1';
  if (pct >= 61) return 'B2';
  if (pct >= 51) return 'C1';
  if (pct >= 41) return 'C2';
  if (pct >= 33) return 'D';
  return 'E';
}

export const BOARD_LABELS = {
  CBSE:  'CBSE',
  ICSE:  'ICSE',
  RBSE:  'Rajasthan Board (RBSE)',
  State: 'State Board',
  Other: 'Other',
};

// CBSE reports an A1-E letter grade; every other board here (ICSE, Rajasthan
// Board, generic State boards) traditionally reports a pass division instead
// of a letter grade. Cutoffs match the commonly-used division bands — a
// school should confirm against its board's current circular before this is
// used on an official certificate, since boards do revise these.
export function resultFor(pct, board) {
  if (pct == null || Number.isNaN(pct)) return '—';
  if (!board || board === 'CBSE') return gradeFor(pct);
  if (pct >= 75) return 'Distinction';
  if (pct >= 60) return 'First Division';
  if (pct >= 45) return 'Second Division';
  if (pct >= 33) return 'Third Division';
  return 'Fail';
}

export const pct = (obtained, max) => (max > 0 ? Math.round((obtained / max) * 1000) / 10 : null);

// Reads an image File, scales it so the longer side is at most `maxSide` px,
// and returns a JPEG data URL — keeps student photos to a few tens of KB.
export function resizeImage(file, maxSide = 480, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return reject(new Error('Please choose an image file'));
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image')); };
    img.src = url;
  });
}

// Indian academic sessions end on 31 March — the default "valid till" for ID cards.
export function sessionEndISO(today = new Date()) {
  const y = today.getMonth() >= 3 ? today.getFullYear() + 1 : today.getFullYear();
  return `${y}-03-31`;
}

// Reads any File as a data URL (for PDFs / documents that shouldn't be resized).
export function readFileAsDataUrl(file, maxBytes = 2 * 1024 * 1024) {
  return new Promise((resolve, reject) => {
    if (file.size > maxBytes) return reject(new Error(`File is too large — max ${Math.round(maxBytes / 1024 / 1024)} MB`));
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(file);
  });
}

// Images are shrunk; PDFs are read as-is. Returns a data URL.
export async function fileToUpload(file) {
  if (file.type.startsWith('image/')) return resizeImage(file, 1600, 0.85);
  if (file.type === 'application/pdf') return readFileAsDataUrl(file);
  throw new Error('Please choose a PDF or an image');
}

// Opens a data URL (PDF/image) in a new tab — browsers block navigating
// straight to data: URLs, so it goes through a Blob.
export function openDataUrl(dataUrl) {
  const [head, b64] = dataUrl.split(',');
  const mime = head.replace('data:', '').replace(';base64', '');
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const PAYMENT_MODES = ['Cash', 'UPI', 'Cheque', 'Bank transfer', 'Card'];
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Date → words for certificates: '2015-04-12' → 'Twelfth April Two Thousand Fifteen'.
const ORD = ['', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth', 'Eleventh', 'Twelfth',
  'Thirteenth', 'Fourteenth', 'Fifteenth', 'Sixteenth', 'Seventeenth', 'Eighteenth', 'Nineteenth', 'Twentieth', 'Twenty-first',
  'Twenty-second', 'Twenty-third', 'Twenty-fourth', 'Twenty-fifth', 'Twenty-sixth', 'Twenty-seventh', 'Twenty-eighth',
  'Twenty-ninth', 'Thirtieth', 'Thirty-first'];
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
  'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const under100 = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? `-${ONES[n % 10]}` : ''}`);
export function yearInWords(y) {
  if (y >= 2000) return `Two Thousand${y % 100 ? ` ${under100(y % 100)}` : ''}`;
  return `${under100(Math.floor(y / 100))} ${under100(y % 100) || 'Hundred'}`.trim();
}
export function dateInWords(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return `${ORD[d]} ${MONTH_NAMES[m - 1]} ${yearInWords(y)}`;
}
